import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Badge from '../components/Badge';
import { fmtDT } from '../utils/helpers';
import api from '../services/api';

interface ITStaff { id: string; name: string; }
interface LogEntry {
  id:             string;
  ticketId:       string;
  category:       string | null;
  requesterName:  string | null;
  currentStatus:  string | null;
  priority:       string | null;
  reassignedBy:   string;
  assignedToId:   string;
  assignedToName: string;
  reason:         string;
  createdAt:      string;
}
interface Page { entries: LogEntry[]; total: number; page: number; limit: number; }

const PAGE_SIZE = 50;
const sel = 'text-sm border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-violet-600 bg-white';

export default function AssignmentLog() {
  const navigate = useNavigate();
  const [itStaff,      setITStaff]      = useState<ITStaff[]>([]);
  const [assignedToId, setAssignedToId] = useState('');
  const [page,          setPage]        = useState(1);
  const [data,          setData]        = useState<Page | null>(null);
  const [loading,       setLoading]     = useState(false);

  useEffect(() => {
    api.get<ITStaff[]>('/admin/users/it-staff-list').then(r => setITStaff(r.data)).catch(() => {});
  }, []);

  useEffect(() => { setPage(1); }, [assignedToId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
      if (assignedToId) params.set('assignedToId', assignedToId);
      const r = await api.get<Page>(`/tickets/assignment-log?${params}`);
      setData(r.data);
    } finally {
      setLoading(false);
    }
  }, [assignedToId, page]);

  useEffect(() => { load(); }, [load]);

  const entries    = data?.entries ?? [];
  const total      = data?.total   ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-4 sm:p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-[#010E23]">Assignment Log</h1>
          <p className="text-xs text-gray-400 mt-0.5">Every time a ticket was handed off to another IT staff member, with the reason and its current status.</p>
        </div>
        <span className="text-xs text-gray-400">{total} total</span>
      </div>

      <div className="flex flex-wrap gap-2">
        <select className={sel} value={assignedToId} onChange={e => setAssignedToId(e.target.value)}>
          <option value="">All Staff</option>
          {itStaff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        {assignedToId && (
          <button className="text-xs text-violet-600 hover:underline" onClick={() => setAssignedToId('')}>
            Clear filter
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="py-16 flex justify-center">
            <div className="w-5 h-5 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : entries.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-400">No reassignments found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ minWidth: 900 }}>
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  {['Ref', 'Requester', 'Category', 'Reassigned By', 'Assigned To', 'Reason', 'When', 'Status'].map(h => (
                    <th key={h} className="text-left text-xs font-semibold text-gray-400 px-4 py-2.5">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entries.map(e => (
                  <tr key={e.id}
                    className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/tickets/${e.ticketId}`)}
                  >
                    <td className="px-4 py-3 font-mono text-xs text-[#26148C]">{e.ticketId}</td>
                    <td className="px-4 py-3 text-xs font-medium text-[#010E23]">{e.requesterName || '—'}</td>
                    <td className="px-4 py-3 text-xs">{e.category || '—'}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{e.reassignedBy}</td>
                    <td className="px-4 py-3 text-xs font-medium text-[#010E23]">{e.assignedToName}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 max-w-[260px] truncate" title={e.reason}>{e.reason}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{fmtDT(e.createdAt)}</td>
                    <td className="px-4 py-3">{e.currentStatus ? <Badge status={e.currentStatus} small /> : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border border-gray-100 rounded-xl bg-gray-50/50">
          <span className="text-xs text-gray-400">
            {total === 0 ? 'No entries' : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total}`}
          </span>
          <div className="flex items-center gap-1">
            <button className="text-xs text-gray-500 disabled:opacity-40 px-2 py-1" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>← Prev</button>
            <span className="text-xs text-gray-500 px-2">{page} / {totalPages}</span>
            <button className="text-xs text-gray-500 disabled:opacity-40 px-2 py-1" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next →</button>
          </div>
        </div>
      )}
    </div>
  );
}
